import { capitalizeString } from '@order-management/format';
import {
  InputType,
  PaginatedAPIResponse,
  PaginatedContentConfig,
  SortDirection,
  WorkOrderDashboardPageTabs,
  WorkOrderStatus,
  WorkOrderSummaryData,
  WorkOrderType,
  WorkOrdersCriteria,
} from '@order-management/types';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Element } from 'react-bulma-components';
import {
  LoaderFunction,
  defer,
  useAsyncValue,
  useLoaderData,
  useNavigation,
  useOutletContext,
  useRevalidator,
  useSearchParams,
} from 'react-router-dom';
import ErrorHandlingAwait from '../../shared/error-handling-await/error-handling-await';
import FilterBar, { FiltersData } from '../../shared/filter-bar/filter-bar';
import useHttpRequest from '../../shared/hooks/http-request';
import useUpdateUrl from '../../shared/hooks/url';
import {
  createSearchParams,
  getSearchParamValue,
  getSearchParamValues,
} from '../../shared/url';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import Spinner from '../../ui/spinner/spinner';
import { OnTableChangeData } from '../../ui/table/types';
import { WorkOrderDashboardPageContext } from '../work-orders-dashboard-page/types';
import WorkOrdersQueueTable from '../work-orders-queue-table/work-orders-queue-table';
import {
  fetchWorkOrders,
  updateWorkOrderPriority,
} from '../work-orders-service';

type QueueWorkOrders = {
  currentlyProcessedWorkOrders: PaginatedAPIResponse<WorkOrderSummaryData>;
  queueWorkOrders: PaginatedAPIResponse<WorkOrderSummaryData>;
};

type QueueWorkOrdersResponse = [
  PaginatedAPIResponse<WorkOrderSummaryData>,
  PaginatedAPIResponse<WorkOrderSummaryData>
];

const PRIORITY_UPDATE_ERROR_MESSAGE =
  'The priority could not be updated. Please try again';

const initialCriteria: WorkOrdersCriteria = {
  itemsPerPage: 10,
  pageNumber: 1,
  search: '',
  sortBy: 'priority',
  status: WorkOrderStatus.QUEUE,
};

export const loader: LoaderFunction = async ({ request }) => {
  const searchParams = createSearchParams(request);

  const currentlyProcessedWorkOrdersPromise = fetchWorkOrders({
    ...initialCriteria,
    priority: '0',
  });

  const queueWorkOrdersPromise = fetchWorkOrders({
    itemsPerPage: parseInt(
      getSearchParamValue(searchParams, 'itemsPerPage') ?? '10'
    ),
    pageNumber: parseInt(
      getSearchParamValue(searchParams, 'pageNumber') ?? '1'
    ),
    search: getSearchParamValue(searchParams, 'search') ?? '',
    sortBy: 'priority',
    sortDirection: SortDirection.ASC,
    workOrderName: getSearchParamValues(searchParams, 'workOrderName'),
    accounts: getSearchParamValues(searchParams, 'accounts'),
    type: getSearchParamValues<WorkOrderType>(searchParams, 'type'),
    createdBy: getSearchParamValues(searchParams, 'createdBy'),
    salesOrders: getSearchParamValues(searchParams, 'salesOrders'),
    status: WorkOrderStatus.QUEUE,
  });

  const response = Promise.all([
    currentlyProcessedWorkOrdersPromise,
    queueWorkOrdersPromise,
  ]);

  return defer({ response });
};

function WorkOrdersQueueTabContent() {
  const asyncValues = useAsyncValue() as QueueWorkOrdersResponse;

  const { setActiveTab, setShowCreateWorkOrderButton } =
    useOutletContext<WorkOrderDashboardPageContext>();

  const navigation = useNavigation();
  const [searchParams] = useSearchParams();
  const updateUrl = useUpdateUrl();

  const revalidator = useRevalidator();

  const { executeHttpRequest, isLoading: isUpdatingPriority } =
    useHttpRequest();

  const response: QueueWorkOrders = useMemo(
    () => ({
      currentlyProcessedWorkOrders: asyncValues[0],
      queueWorkOrders: asyncValues[1],
    }),
    [asyncValues]
  );

  const [criteria, setCriteria] = useState<WorkOrdersCriteria>({
    itemsPerPage: parseInt(
      getSearchParamValue(searchParams, 'itemsPerPage') ?? '10'
    ),
    pageNumber: parseInt(
      getSearchParamValue(searchParams, 'pageNumber') ?? '1'
    ),
    search: getSearchParamValue(searchParams, 'search') ?? '',
    sortBy: 'priority',
    sortDirection: SortDirection.ASC,
    workOrderName: getSearchParamValues(searchParams, 'workOrderName'),
    accounts: getSearchParamValues(searchParams, 'accounts'),
    type: getSearchParamValues<WorkOrderType>(searchParams, 'type'),
    createdBy: getSearchParamValues(searchParams, 'createdBy'),
    salesOrders: getSearchParamValues(searchParams, 'salesOrders'),
    status: WorkOrderStatus.QUEUE,
  });

  const currentlyBeingProcessedWorkOrder = useMemo(
    () => response?.currentlyProcessedWorkOrders.data ?? [],
    [response?.currentlyProcessedWorkOrders.data]
  );

  const queueWorkOrders = useMemo(
    () => response?.queueWorkOrders.data ?? [],
    [response]
  );

  const tableConfig: PaginatedContentConfig = useMemo(
    () => ({
      ...criteria,
      pages: response?.queueWorkOrders.pages ?? 1,
    }),
    [criteria, response?.queueWorkOrders.pages]
  );

  const isDataLoading = useMemo(
    () => navigation.state === 'loading',
    [navigation.state]
  );

  const filtersData = useMemo(() => {
    return {
      workOrderName: {
        title: 'Work order name',
        appliedCount: criteria.workOrderName?.length,
        value: criteria.workOrderName,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'workOrderName',
          valueKey: 'workOrderName',
          placeholder: 'Search by Work order name',
        },
      },
      accounts: {
        title: 'Account',
        appliedCount: criteria.accounts?.length,
        value: criteria.accounts,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'accounts',
          valueKey: 'accounts',
          placeholder: 'Search by Account',
        },
      },
      salesOrders: {
        title: 'Sales order',
        appliedCount: criteria.salesOrders?.length,
        value: criteria.salesOrders,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'salesOrders',
          valueKey: 'salesOrders',
          placeholder: 'Search by SO',
        },
      },
      type: {
        title: 'Type',
        appliedCount: criteria.type?.length,
        value: criteria.type,
        input: {
          type: InputType.SELECT,
          valueKey: 'type',
          options: Object.values(WorkOrderType).map((value) => ({
            label: capitalizeString(value.replace('_', ' ')),
            value,
          })),
        },
      },
      createdBy: {
        title: 'Created by',
        appliedCount: criteria.createdBy?.length,
        value: criteria.createdBy,
        input: {
          type: InputType.FILTER_CRITERIA,
          filterValueKey: 'createdBy',
          valueKey: 'createdBy',
          placeholder: 'Search by Creator',
        },
      },
    };
  }, [
    criteria.accounts,
    criteria.createdBy,
    criteria.salesOrders,
    criteria.type,
    criteria.workOrderName,
  ]) as FiltersData<WorkOrdersCriteria>;

  const appliedFilters = useMemo(() => {
    return Object.entries(filtersData).filter(
      ([_, filter]) => filter.appliedCount
    );
  }, [filtersData]);

  const emptyContentText = useMemo(() => {
    const hasAppliedFilters =
      criteria.search !== '' || appliedFilters.length > 0;
    return hasAppliedFilters
      ? 'There are no work orders in queue that match the selected criteria'
      : 'There are no work orders in queue at the moment';
  }, [appliedFilters.length, criteria.search]);

  const revalidate = useCallback(() => {
    revalidator.revalidate();
  }, [revalidator]);

  const handlePriorityUpdate = useCallback(
    (id: string, priority: number) => {
      executeHttpRequest({
        asyncFunction: async () => {
          await updateWorkOrderPriority(id, priority);
          revalidate();
        },
        customErrorMessage: PRIORITY_UPDATE_ERROR_MESSAGE,
      });
    },
    [executeHttpRequest, revalidate]
  );

  const handleOnTableChange = useCallback(
    (value: OnTableChangeData<WorkOrderSummaryData>): void => {
      const newData = {
        ...criteria,
        ...value.config,
        sortBy: value.column?.key || criteria.sortBy,
      };

      setCriteria(newData);
      updateUrl(newData);
    },
    [criteria, updateUrl]
  );

  const handleTableFilterSubmit = useCallback(
    (value: WorkOrdersCriteria): void => {
      const newCriteria: WorkOrdersCriteria = {
        ...value,
        itemsPerPage: criteria.itemsPerPage,
        pageNumber: 1,
        search: criteria.search,
        sortBy: criteria.sortBy,
        status: WorkOrderStatus.QUEUE,
      };
      setCriteria(newCriteria);
      updateUrl(newCriteria);
    },
    [criteria, setCriteria, updateUrl]
  );

  const onRemoveFilters = useCallback(
    (filterDataKeys: (keyof WorkOrdersCriteria)[]) => {
      const newData = { ...criteria };
      for (const filterDataKey of filterDataKeys) {
        const initialFilterValue = initialCriteria[filterDataKey];
        if (initialFilterValue === undefined) {
          delete newData[filterDataKey];
        } else {
          newData[filterDataKey] = initialFilterValue as string &
            string[] &
            number;
        }
      }
      handleTableFilterSubmit(newData);
    },
    [criteria, handleTableFilterSubmit]
  );

  useEffect(() => {
    setActiveTab(WorkOrderDashboardPageTabs.QUEUE);
  }, [setActiveTab]);

  useEffect(() => {
    setShowCreateWorkOrderButton(true);
  }, [setShowCreateWorkOrderButton]);

  if (isDataLoading) {
    return (
      <Element display="flex" justifyContent="center">
        <Spinner />
      </Element>
    );
  }

  return (
    <Element>
      <h1 className="is-size-4 mb-6 has-text-weight-light">
        Currently being processed
      </h1>
      <WorkOrdersQueueTable
        dataSource={currentlyBeingProcessedWorkOrder}
        config={tableConfig}
        form={{ show: false }}
        empty={
          <Element display="flex" justifyContent="center" className="mb-6">
            <span className="has-text-weight-light is-size-5">
              There are no work orders currently being processed
            </span>
          </Element>
        }
      />
      <h1 className="is-size-4 mb-4 has-text-weight-light">Queue</h1>
      <Element display="flex" className="w-full mb-2">
        <FilterBar
          initialData={initialCriteria}
          data={criteria}
          filtersData={filtersData}
          onFilterSubmit={handleTableFilterSubmit}
          onRemoveFilter={onRemoveFilters}
        />
      </Element>
      <WorkOrdersQueueTable
        dataSource={queueWorkOrders}
        config={tableConfig}
        onChange={handleOnTableChange}
        includePriority
        onPriorityUpdate={handlePriorityUpdate}
        isUpdatingPriority={
          isUpdatingPriority || revalidator.state === 'loading'
        }
        empty={
          <Element display="flex" justifyContent="center" className="my-2">
            <span className="has-text-weight-light">{emptyContentText}</span>
          </Element>
        }
        form={{
          enableSearch: false,
        }}
      />
    </Element>
  );
}

export function WorkOrdersQueueTab() {
  const { response } = useLoaderData() as {
    response: Promise<QueueWorkOrdersResponse>;
  };

  return (
    <Suspense fallback={<PageSpinner />}>
      <ErrorHandlingAwait resolve={response}>
        <WorkOrdersQueueTabContent />
      </ErrorHandlingAwait>
    </Suspense>
  );
}

export default WorkOrdersQueueTab;
